import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { Control, useController, useForm } from 'react-hook-form';
import { Alert, Stack, Unstable_Grid2 as Grid } from '@mui/material';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import * as yup from 'yup';

import {
  AMCR_FINDER_NAME_PATTERN,
  AMCR_MAX_DEPTH_CM,
  DEFAULT_FIND_COUNT,
  DEFAULT_FIND_DEPTH_CM,
  DEFAULT_FIND_SITUATION_TYPE,
  FIND_SITUATION_TYPES,
} from '../../consts';
import { useYupValidationResolver } from '../../helpers';
import { useVocabulary } from '../../i18n';
import { amcrPreviewMessages, isCoordinate, toNumber } from '../AmcrPasDialog';
import {
  DateTimeField,
  FieldLabel,
  FormField,
  MaterialAutocomplete,
  SurfaceAutocomplete,
  SurfaceTextField,
} from '../design-system';
import { FindPasSummary } from './FindPasSummary';
import { PasFind, getSituationTypeLabel, pasFieldLabels } from './pasFind';
import { findPasFieldLabels } from './messages';

export interface FindTypeOption {
  id: string;
  category?: string | null;
}

export interface EraOption {
  id: string;
  label: string;
  parentLabel: string;
}

export interface FindPasOptions {
  types: FindTypeOption[];
  materials: string[];
  eras: EraOption[];
}

export interface FindPasInput {
  amcrId: string;
  foundBy: string | null;
  type?: string;
  material?: string;
  era?: string;
  situationType: string | null;
  depth: number | null;
  count: number | null;
  foundAt?: string;
  latitude?: number;
  longitude?: number;
  localization: string;
  description: string;
}

export interface FindPasFormValues {
  amcrId: string;
  foundBy: string;
  type: FindTypeOption | null;
  material: string | null;
  era: EraOption | null;
  situationType: string | null;
  depth: string;
  count: string;
  foundAt: DateTime | null;
  latitude: string;
  longitude: string;
  localization: string;
  description: string;
}

interface FindPasEditFormProps {
  find: PasFind;
  options: FindPasOptions;
  /** The user's own AMCR name, "anonym", and previously used finder names. */
  finderNames: string[];
  disabled: boolean;
  onSubmit: (input: FindPasInput) => void;
}

type FormControl = Control<FindPasFormValues>;
type TextFieldName =
  | 'amcrId'
  | 'depth'
  | 'count'
  | 'latitude'
  | 'longitude'
  | 'localization'
  | 'description';

export const FIND_PAS_EDIT_FORM_ID = 'find-pas-edit-form';

const errorListStyles = { m: 0, pl: 2 };
const depthPlaceholder = String(DEFAULT_FIND_DEPTH_CM);
const countPlaceholder = String(DEFAULT_FIND_COUNT);

const toText = (value: number | string | null | undefined) =>
  value == null ? '' : String(value);

const getOptionId = (option: { id: string }) => option.id;
const isSameOption = (option: { id: string }, value: { id: string }) =>
  option.id === value.id;
const getEraLabel = (option: EraOption) => option.label;
const groupEraOption = (option: EraOption) => option.parentLabel;

/** Keeps a stored value selectable even when the option list lacks it. */
const withOption = <Option extends { id: string }>(
  options: Option[],
  option: Option | null
) =>
  !option || options.some(({ id }) => id === option.id)
    ? options
    : [option, ...options];

const isValidCoordinatePair = ({ latitude, longitude }: FindPasFormValues) => {
  if (!latitude.trim() && !longitude.trim()) {
    return true;
  }

  return isCoordinate(latitude, 90) && isCoordinate(longitude, 180);
};

const toInput = (values: FindPasFormValues): FindPasInput => ({
  amcrId: values.amcrId.trim(),
  foundBy: values.foundBy.trim() || null,
  type: values.type?.id,
  material: values.material || undefined,
  era: values.era?.id,
  situationType: values.situationType,
  depth: toNumber(values.depth),
  count: toNumber(values.count),
  foundAt: values.foundAt?.isValid
    ? values.foundAt.toISO() || undefined
    : undefined,
  latitude: toNumber(values.latitude) ?? undefined,
  longitude: toNumber(values.longitude) ?? undefined,
  localization: values.localization.trim(),
  description: values.description,
});

interface TextInputProps {
  control: FormControl;
  name: TextFieldName;
  label: MessageDescriptor;
  disabled: boolean;
  type?: string;
  multiline?: boolean;
  placeholder?: string;
}

const TextInput = ({
  control,
  name,
  label,
  disabled,
  type = 'text',
  multiline,
  placeholder,
}: TextInputProps) => {
  const { field } = useController<FindPasFormValues, TextFieldName>({
    control,
    name,
  });
  const id = `find-pas-${name}`;

  return (
    <FormField clampLabel>
      <FieldLabel htmlFor={id}>
        <FormattedMessage {...label} />
      </FieldLabel>
      <SurfaceTextField
        fullWidth
        id={id}
        type={type}
        disabled={disabled}
        multiline={multiline}
        minRows={multiline ? 2 : undefined}
        placeholder={placeholder}
        surface="sunken"
        {...field}
      />
    </FormField>
  );
};

interface FinderInputProps {
  control: FormControl;
  options: string[];
  disabled: boolean;
}

/** Offers known finders but accepts any new "Surname, Given name". */
const FinderInput = ({ control, options, disabled }: FinderInputProps) => {
  const { field } = useController<FindPasFormValues, 'foundBy'>({
    control,
    name: 'foundBy',
  });

  const handleChange = (_event: SyntheticEvent, value: string | null) => {
    field.onChange(value ?? '');
  };

  const handleInputChange = (_event: SyntheticEvent, value: string) => {
    field.onChange(value);
  };

  const textFieldProps = {
    id: 'find-pas-foundBy',
    inputRef: field.ref,
    onBlur: field.onBlur,
  };

  return (
    <FormField clampLabel>
      <FieldLabel htmlFor="find-pas-foundBy">
        <FormattedMessage {...findPasFieldLabels.foundBy} />
      </FieldLabel>
      <SurfaceAutocomplete<string, false, false, true>
        freeSolo
        forcePopupIcon
        options={options}
        value={field.value}
        inputValue={field.value}
        disabled={disabled}
        onChange={handleChange}
        onInputChange={handleInputChange}
        surface="sunken"
        textFieldProps={textFieldProps}
      />
    </FormField>
  );
};

interface TypeInputProps {
  control: FormControl;
  options: FindTypeOption[];
  disabled: boolean;
}

const TypeInput = ({ control, options, disabled }: TypeInputProps) => {
  const vocabulary = useVocabulary();
  const { field } = useController<FindPasFormValues, 'type'>({
    control,
    name: 'type',
  });

  const getTypeLabel = useCallback(
    (option: FindTypeOption) => vocabulary('findType', option.id),
    [vocabulary]
  );
  const groupTypeOption = useCallback(
    (option: FindTypeOption) => vocabulary('findType', option.category),
    [vocabulary]
  );

  const handleChange = (
    _event: SyntheticEvent,
    value: FindTypeOption | null
  ) => {
    field.onChange(value);
  };

  const textFieldProps = {
    id: 'find-pas-type',
    inputRef: field.ref,
    onBlur: field.onBlur,
  };

  return (
    <FormField clampLabel>
      <FieldLabel htmlFor="find-pas-type">
        <FormattedMessage {...pasFieldLabels.type} />
      </FieldLabel>
      <SurfaceAutocomplete<FindTypeOption>
        options={options}
        value={field.value}
        disabled={disabled}
        groupBy={groupTypeOption}
        getOptionLabel={getTypeLabel}
        getOptionKey={getOptionId}
        isOptionEqualToValue={isSameOption}
        onChange={handleChange}
        surface="sunken"
        textFieldProps={textFieldProps}
      />
    </FormField>
  );
};

interface MaterialInputProps {
  control: FormControl;
  options: string[];
  disabled: boolean;
}

const MaterialInput = ({ control, options, disabled }: MaterialInputProps) => {
  const vocabulary = useVocabulary();
  const { field } = useController<FindPasFormValues, 'material'>({
    control,
    name: 'material',
  });

  const getMaterialLabel = useCallback(
    (material: string) => vocabulary('material', material),
    [vocabulary]
  );

  const handleChange = (_event: SyntheticEvent, value: string | null) => {
    field.onChange(value);
  };

  return (
    <FormField clampLabel>
      <FieldLabel htmlFor="find-pas-material">
        <FormattedMessage {...pasFieldLabels.material} />
      </FieldLabel>
      <MaterialAutocomplete
        id="find-pas-material"
        options={options}
        value={field.value}
        disabled={disabled}
        getOptionLabel={getMaterialLabel}
        inputName={field.name}
        inputRef={field.ref}
        onInputBlur={field.onBlur}
        onChange={handleChange}
      />
    </FormField>
  );
};

interface EraInputProps {
  control: FormControl;
  options: EraOption[];
  disabled: boolean;
}

const EraInput = ({ control, options, disabled }: EraInputProps) => {
  const { field } = useController<FindPasFormValues, 'era'>({
    control,
    name: 'era',
  });

  const handleChange = (_event: SyntheticEvent, value: EraOption | null) => {
    field.onChange(value);
  };

  const textFieldProps = {
    id: 'find-pas-era',
    inputRef: field.ref,
    onBlur: field.onBlur,
  };

  return (
    <FormField clampLabel>
      <FieldLabel htmlFor="find-pas-era">
        <FormattedMessage {...pasFieldLabels.era} />
      </FieldLabel>
      <SurfaceAutocomplete<EraOption>
        options={options}
        value={field.value}
        disabled={disabled}
        groupBy={groupEraOption}
        getOptionLabel={getEraLabel}
        getOptionKey={getOptionId}
        isOptionEqualToValue={isSameOption}
        onChange={handleChange}
        surface="sunken"
        textFieldProps={textFieldProps}
      />
    </FormField>
  );
};

interface SituationTypeInputProps {
  control: FormControl;
  disabled: boolean;
}

const SituationTypeInput = ({ control, disabled }: SituationTypeInputProps) => {
  const intl = useIntl();
  const { field } = useController<FindPasFormValues, 'situationType'>({
    control,
    name: 'situationType',
  });

  const getSituationLabel = useCallback(
    (situationType: string) => getSituationTypeLabel(intl, situationType),
    [intl]
  );

  const handleChange = (_event: SyntheticEvent, value: string | null) => {
    field.onChange(value);
  };

  const textFieldProps = {
    id: 'find-pas-situationType',
    inputRef: field.ref,
    onBlur: field.onBlur,
    placeholder: getSituationLabel(DEFAULT_FIND_SITUATION_TYPE),
  };

  return (
    <FormField clampLabel>
      <FieldLabel htmlFor="find-pas-situationType">
        <FormattedMessage {...findPasFieldLabels.situationType} />
      </FieldLabel>
      <SurfaceAutocomplete<string>
        options={FIND_SITUATION_TYPES}
        value={field.value}
        disabled={disabled}
        getOptionLabel={getSituationLabel}
        onChange={handleChange}
        surface="sunken"
        textFieldProps={textFieldProps}
      />
    </FormField>
  );
};

interface FoundAtInputProps {
  control: FormControl;
  disabled: boolean;
}

const FoundAtInput = ({ control, disabled }: FoundAtInputProps) => {
  const { field } = useController<FindPasFormValues, 'foundAt'>({
    control,
    name: 'foundAt',
  });

  return (
    <DateTimeField
      clampLabel
      id="find-pas-foundAt"
      label={<FormattedMessage {...pasFieldLabels.foundAt} />}
      disabled={disabled}
      inputRef={field.ref}
      name={field.name}
      onBlur={field.onBlur}
      onChange={field.onChange}
      value={field.value}
    />
  );
};

/**
 * Edits the Acolyte values a PAS record is built from. Values use the
 * Acolyte vocabulary and are saved to the find, not sent to PAS.
 */
const FindPasEditForm = React.memo(
  ({
    find,
    options,
    finderNames,
    disabled,
    onSubmit,
  }: FindPasEditFormProps) => {
    const intl = useIntl();
    const vocabulary = useVocabulary();
    const [errors, setErrors] = useState<Array<{ error: string }>>([]);

    const defaultValues = useMemo<FindPasFormValues>(() => {
      const type = find.type
        ? options.types.find(({ id }) => id === find.type) ?? {
            id: find.type,
          }
        : null;
      const eraId = find.era?.toLocaleLowerCase();
      const era = eraId
        ? options.eras.find(({ id }) => id === eraId) ?? {
            id: eraId,
            label: vocabulary('era', eraId),
            parentLabel: '',
          }
        : null;

      return {
        amcrId: find.amcrId ?? '',
        foundBy: find.foundBy ?? '',
        type,
        material: find.material || null,
        era,
        situationType: find.situationType || null,
        depth: toText(find.depth),
        count: toText(find.count),
        foundAt: find.foundAt ? DateTime.fromISO(find.foundAt) : null,
        latitude: toText(find.latitude),
        longitude: toText(find.longitude),
        localization: find.localization ?? '',
        description: find.description ?? '',
      };
    }, [find, options, vocabulary]);

    const typeOptions = useMemo(
      () => withOption(options.types, defaultValues.type),
      [defaultValues.type, options.types]
    );
    const eraOptions = useMemo(
      () => withOption(options.eras, defaultValues.era),
      [defaultValues.era, options.eras]
    );
    const finderOptions = useMemo(
      () =>
        find.foundBy
          ? [...new Set([find.foundBy, ...finderNames])]
          : finderNames,
      [find.foundBy, finderNames]
    );

    const validationSchema = useMemo(
      () =>
        yup.object({
          depth: yup
            .string()
            .matches(/^\d*$/, intl.formatMessage(amcrPreviewMessages.depth))
            .test(
              'max-depth',
              intl.formatMessage(amcrPreviewMessages.depth),
              (value) => Number(value) <= AMCR_MAX_DEPTH_CM
            ),
          count: yup
            .string()
            .matches(
              /^([1-9]\d*)?$/,
              intl.formatMessage(amcrPreviewMessages.count)
            ),
          latitude: yup
            .string()
            .test(
              'coordinates',
              intl.formatMessage(amcrPreviewMessages.coordinates),
              (_value, context) => isValidCoordinatePair(context.parent)
            ),
          foundBy: yup
            .string()
            .trim()
            .test(
              'finder',
              intl.formatMessage(amcrPreviewMessages.finderName),
              (value) =>
                !value ||
                finderOptions.includes(value) ||
                AMCR_FINDER_NAME_PATTERN.test(value)
            ),
        }),
      [intl, finderOptions]
    );
    const resolver = useYupValidationResolver(validationSchema, setErrors);
    const { control, handleSubmit } = useForm<FindPasFormValues>({
      defaultValues,
      resolver,
    });

    const handleValidSubmit = (values: FindPasFormValues) => {
      setErrors([]);
      onSubmit(toInput(values));
    };

    return (
      <Stack
        component="form"
        id={FIND_PAS_EDIT_FORM_ID}
        noValidate
        gap={2}
        onSubmit={handleSubmit(handleValidSubmit)}
      >
        <FindPasSummary control={control} />
        <Grid container spacing={2}>
          <Grid xs={12} sm={6}>
            <TextInput
              control={control}
              name="amcrId"
              label={findPasFieldLabels.amcrId}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <FinderInput
              control={control}
              options={finderOptions}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <TypeInput
              control={control}
              options={typeOptions}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <MaterialInput
              control={control}
              options={options.materials}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <EraInput
              control={control}
              options={eraOptions}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <SituationTypeInput control={control} disabled={disabled} />
          </Grid>
          <Grid xs={6} sm={3}>
            <TextInput
              control={control}
              name="depth"
              type="number"
              label={findPasFieldLabels.depth}
              placeholder={depthPlaceholder}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={6} sm={3}>
            <TextInput
              control={control}
              name="count"
              type="number"
              label={findPasFieldLabels.count}
              placeholder={countPlaceholder}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <FoundAtInput control={control} disabled={disabled} />
          </Grid>
          <Grid xs={6} sm={3}>
            <TextInput
              control={control}
              name="latitude"
              label={findPasFieldLabels.latitude}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={6} sm={3}>
            <TextInput
              control={control}
              name="longitude"
              label={findPasFieldLabels.longitude}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <TextInput
              control={control}
              name="localization"
              label={pasFieldLabels.localization}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={12}>
            <TextInput
              multiline
              control={control}
              name="description"
              label={findPasFieldLabels.description}
              disabled={disabled}
            />
          </Grid>
        </Grid>
        {errors.length > 0 && (
          <Alert severity="error" variant="outlined">
            <Stack component="ul" sx={errorListStyles}>
              {errors.map(({ error }) => (
                <li key={error}>{error}</li>
              ))}
            </Stack>
          </Alert>
        )}
      </Stack>
    );
  }
);

FindPasEditForm.displayName = 'FindPasEditForm';

export { FindPasEditForm };
